import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, Send, Facebook } from 'lucide-react';
import { businessInfo } from '../mockData';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    device: '',
    issue: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast({
        title: "Message Sent!",
        description: "Thanks! We received your inquiry and will get back to you shortly.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        device: '',
        issue: '',
        message: ''
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please call us instead.";
      toast({
        title: "Couldn't send message",
        description: detail,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* Header Section */}
      <section className="py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Get In Touch
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Have a question or need a repair? We're here to help!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        data-testid="contact-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-neutral-800 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-neutral-800 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        data-testid="contact-phone-input"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-neutral-800 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all"
                        placeholder="(915) 123-4567"
                      />
                    </div>

                    <div>
                      <label htmlFor="device" className="block text-sm font-medium text-gray-300 mb-2">
                        Device Type
                      </label>
                      <select
                        id="device"
                        name="device"
                        value={formData.device}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-black border border-neutral-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all"
                      >
                        <option value="">Select device type</option>
                        <option value="iphone">iPhone</option>
                        <option value="samsung">Samsung</option>
                        <option value="google">Google Pixel</option>
                        <option value="lg">LG</option>
                        <option value="motorola">Motorola</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="issue" className="block text-sm font-medium text-gray-300 mb-2">
                      Type of Issue
                    </label>
                    <select
                      id="issue"
                      name="issue"
                      value={formData.issue}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-black border border-neutral-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all"
                    >
                      <option value="">Select issue type</option>
                      <option value="screen">Cracked/Broken Screen</option>
                      <option value="battery">Battery Issues</option>
                      <option value="charging">Charging Port Problems</option>
                      <option value="water">Water Damage</option>
                      <option value="software">Software Issues</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 bg-black border border-neutral-800 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all resize-none"
                      placeholder="Tell us about your device issue or question..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-black font-bold text-white font-semibold rounded-lg transition-all shadow-lg shadow-white/30 hover:shadow-white/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              {/* Quick Contact */}
              <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-6">Quick Contact</h3>
                <div className="space-y-4">
                  <a 
                    href={`tel:${businessInfo.phone}`}
                    className="flex items-start gap-4 p-4 bg-black border border-neutral-800 rounded-lg hover:border-white/50 transition-all group"
                  >
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Call Us</p>
                      <p className="text-white font-semibold">{businessInfo.phone}</p>
                    </div>
                  </a>

                  <a 
                    href={`mailto:${businessInfo.email}`}
                    className="flex items-start gap-4 p-4 bg-black border border-neutral-800 rounded-lg hover:border-white/50 transition-all group"
                  >
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Email Us</p>
                      <p className="text-white font-semibold break-all">{businessInfo.email}</p>
                    </div>
                  </a>

                  <a 
                    href={businessInfo.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 bg-black border border-neutral-800 rounded-lg hover:border-white/50 transition-all group"
                  >
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                      <Facebook className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Follow Us</p>
                      <p className="text-white font-semibold">Facebook</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Location & Hours */}
              <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-6">Location & Hours</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-white flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Address</p>
                      <p className="text-white">{businessInfo.address}</p>
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(businessInfo.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white hover:text-white-300 text-sm mt-2 inline-block"
                      >
                        Get Directions →
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-white flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Hours</p>
                      <p className="text-white font-medium">Open Daily</p>
                      <p className="text-white">Closes at 2:00 AM</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Walk-ins Welcome */}
              <div className="bg-gradient-to-br from-white/10 to-teal-500/10 border border-white/20 rounded-2xl p-6 text-center">
                <p className="text-white font-bold text-lg mb-2">Walk-Ins Welcome!</p>
                <p className="text-gray-300 text-sm">
                  No appointment needed. Come by anytime during business hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3397.4!2d-106.3!3d31.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDQ4JzAwLjAiTiAxMDbCsDE4JzAwLjAiVw!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Evolution Cell Phone Repairs Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
